import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { verifyEmailOtp, verifyMobileOtp, verifyLoginOtp, resendOtp, resendRegisterOtp, logout } from "../../services/authService";
import { handleApiError } from "../../utils/handleApiError";
import { getUserLocation } from "../../utils/locationHelper";
import { useKyc } from "../../context/KycContext";
import logo from "../../assets/Klyropay.png";
import api from "../../services/api";

function OtpInputs({ value, onChange, length = 6, disabled }) {
  const inputs = useRef([]);
  return (
    <div className="flex flex-wrap justify-center  gap-2 sm:gap-3">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, "");
            const newVal = value.split("");
            newVal[i] = val;
            onChange(newVal.join(""));
            if (val && i < length - 1) inputs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !value[i] && i > 0) inputs.current[i - 1]?.focus();
          }}
          disabled={disabled}
          className="w-9 h-10 sm:w-10 sm:h-11 md:w-11 md:h-12 lg:w-12 lg:h-12
            text-center border rounded-lg
            text-sm sm:text-base md:text-lg
            bg-gray-100 focus:bg-white focus:border-[#4B2364]
            outline-none transition"
        />
      ))}
    </div>
  );
}

function useResendTimer(initial = 60) {
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);
  const start = () => {
    setTimer(initial);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((t) => { if (t <= 1) { clearInterval(intervalRef.current); return 0; } return t - 1; });
    }, 1000);
  };
  useEffect(() => () => clearInterval(intervalRef.current), []);
  return { timer, start };
}

export default function VerifyCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const email   = location.state?.email   || "";
  const mobile  = location.state?.mobile  || "";
  const user_id = location.state?.user_id || "";
  const source  = location.state?.source  || "register";

  useEffect(() => {
    if (!location.state?.email) navigate("/dashboard", { replace: true });
  }, []);

  const [emailOtp, setEmailOtp] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [loading, setLoading]   = useState(false);
  const emailTimer  = useResendTimer(60);
  const mobileTimer = useResendTimer(60);

  useEffect(() => {
    emailTimer.start();
    if (mobile) mobileTimer.start();
  }, []);

  const handleResend = async () => {
    try {
      const fn = source === "login" ? resendOtp : resendRegisterOtp;
      await fn({ type: "email", user_id, email, mobile });
      toast.info("OTP resent to your email.");
      emailTimer.start();
    } catch (err) { handleApiError(err); }
  };

  const handleResendMobile = async () => {
    try {
      const fn = source === "login" ? resendOtp : resendRegisterOtp;
      await fn({ type: "mobile", user_id, email, mobile });
      toast.info("OTP resent to your mobile.");
      mobileTimer.start();
    } catch (err) { handleApiError(err); }
  };

  const { refreshKycStatus } = useKyc();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (emailOtp.length < 6) { toast.error("Enter 6-digit email OTP."); return; }
    if (source === "register" && mobile && mobileOtp.length < 6) {
      toast.error("Enter 6-digit mobile OTP."); return;
    }
    setLoading(true);
    try {
      if (source === "login") {
        const loc = await getUserLocation();
        if (!loc) return;
        const res = await verifyLoginOtp({ user_id, otp: emailOtp, latitude: loc.latitude, longitude: loc.longitude });
        const token = res.data?.token || res.data?.data?.token;
        if (token) localStorage.setItem("token", token);
      } else {
        console.log("Email OTP API called");
        console.log("Mobile OTP API called");
        const [emailRes] = await Promise.all([
          verifyEmailOtp({ user_id, email, otp: emailOtp }),
          verifyMobileOtp({ user_id, mobile, otp: mobileOtp }),
        ]);
        const token = emailRes.data?.token || emailRes.data?.data?.token;
        if (token) localStorage.setItem("token", token);
      }
      toast.success("Verification successful!");
      const kycResult = await refreshKycStatus();
      if (kycResult?.kyc_submitted) { navigate("/dashboard"); } else { navigate("/kyc"); }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch (_) {}
    finally {
      localStorage.removeItem("token");
      toast.success("Logged out successfully.");
      navigate("/");
    }
  };

  return (

<div className="min-h-screen w-full flex items-center justify-center bg-[#F4F2F7] md:px-16 px-4 py-8">

  <div className="w-full md:w-full lg:max-w-md bg-white rounded-2xl shadow-lg lg:px-6 md:px-10 sm:px-8 px-8 py-8">

    {/* Logo */}
    <div className="flex justify-center">
      <img src={logo} alt="logo" className="sm:h-20 md:h-24 w-auto object-contain mb-4" />
    </div>

    {/* Title */}
    <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#34094D]">
      Verify Security Code
    </h2>

    <p className="text-center text-sm text-[#6B6570] mt-2 mb-6">
      We've sent a 6-digit verification code
    </p>

    <form onSubmit={handleSubmit} className="space-y-6">

      {/* EMAIL OTP */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs font-bold text-gray-500">EMAIL ADDRESS</p>
            <p className="text-sm font-semibold text-[#191C1D] break-all">
              {email}
            </p>
          </div>

          {/* <button
            type="button"
            onClick={handleResend}
            disabled={loading || emailTimer.timer > 0}
            className="text-xs px-3 py-1.5 bg-[#4B2364] text-white rounded-md disabled:opacity-50"
          >
            Resend OTP
          </button> */}
        </div>

        <OtpInputs
          value={emailOtp}
          onChange={setEmailOtp}
          disabled={loading}
        />

        <div className="mt-2 text-xs">
          {emailTimer.timer > 0 ? (
            <span className="text-gray-400">
              Resend in {emailTimer.timer}s
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              className="text-[#EA8534] font-semibold"
            >
              RESEND OTP
            </button>
          )}
        </div>
      </div>

      {/* MOBILE OTP */}
      {mobile && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-bold text-gray-500">PHONE NUMBER</p>
              <p className="text-sm font-semibold text-[#191C1D] break-all">
                {mobile}
              </p>
            </div>

            {/* <button
              type="button"
              onClick={handleResendMobile}
              disabled={loading || mobileTimer.timer > 0}
              className="text-xs px-3 py-1.5 bg-[#4B2364] text-white rounded-md disabled:opacity-50"
            >
              Resend OTP
            </button> */}
          </div>

          <OtpInputs
            value={mobileOtp}
            onChange={setMobileOtp}
            disabled={loading}
          />

          <div className="mt-2 text-xs">
            {mobileTimer.timer > 0 ? (
              <span className="text-gray-400">
                Resend in {mobileTimer.timer}s
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResendMobile}
                className="text-[#EA8534] font-semibold"
              >
                RESEND OTP
              </button>
            )}
          </div>
        </div>
      )}

      {/* BUTTON */}
      <button
        type="submit"
        disabled={loading}
        className={`w-full py-3 rounded-xl text-white font-semibold text-sm
        bg-gradient-to-r from-[#EA8534] to-[#A0111A] shadow-md
        flex items-center justify-center gap-2
        ${loading ? "opacity-70" : "hover:opacity-90"}`}
      >
        {loading && (
          <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="white" strokeOpacity="0.3" strokeWidth="2" fill="none" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="2" fill="none" />
          </svg>
        )}
        {loading ? "Verifying..." : "Continue"}
      </button>

      {/* FOOTER */}
      <p className="text-center text-xs text-gray-500">
        Having trouble?{" "}
        <span className="text-[#34094D] font-semibold cursor-pointer">
          Contact Support
        </span>
      </p>

      <p
         onClick={() => navigate("/login", { replace: true })}
        className="text-center text-xs text-gray-500 cursor-pointer hover:text-black"
      >
        ← Back to Merchant Login
      </p>

    </form>
  </div>
</div>
  );
}
